import { Component, OnInit, forwardRef, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';

@Component({
  selector: 'app-date-range-selector',
  templateUrl: './date-range-selector.component.html',
  styleUrls: ['./date-range-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeSelectorComponent),
      multi: true
    }
  ]
})
export class DateRangeSelectorComponent implements OnInit, ControlValueAccessor {
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Output() dateRangeSelected = new EventEmitter<{ start: Date, end: Date }>();
  @Input() value: { start: Date, end: Date } | null = null;

  leftMonth: Date = new Date();
  rightMonth: Date = new Date();
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  isSelecting = false;

  // For ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private dateAdapter: DateAdapter<Date>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initialize right month as one month ahead
    this.rightMonth = this.addMonths(new Date(), 1);

    // Set initial values if provided
    if (this.value && this.value.start && this.value.end) {
      this.selectedStartDate = new Date(this.value.start);
      this.selectedEndDate = new Date(this.value.end);
    }
  }

  // Navigation methods
  previousLeftMonth(): void {
    this.leftMonth = this.addMonths(this.leftMonth, -1);
    // Keep right month at least one month ahead
    if (this.getMonthDiff(this.leftMonth, this.rightMonth) < 1) {
      this.rightMonth = this.addMonths(this.leftMonth, 1);
    }
  }

  nextLeftMonth(): void {
    // Don't allow left month to overlap with right month
    const nextMonth = this.addMonths(this.leftMonth, 1);
    if (this.getMonthDiff(nextMonth, this.rightMonth) >= 0) {
      this.leftMonth = nextMonth;
    }
  }

  previousRightMonth(): void {
    // Don't allow right month to overlap with left month
    const prevMonth = this.addMonths(this.rightMonth, -1);
    if (this.getMonthDiff(this.leftMonth, prevMonth) >= 0) {
      this.rightMonth = prevMonth;
    }
  }

  nextRightMonth(): void {
    this.rightMonth = this.addMonths(this.rightMonth, 1);
  }

  onDateClick(date: Date): void {
    console.log('onDateClick called with date:', date);

    // Create a new date to avoid reference issues
    const clickedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (!this.isSelecting || !this.selectedStartDate) {
      this.selectedStartDate = clickedDate;
      this.selectedEndDate = new Date(clickedDate);
      this.isSelecting = true;
    } else {
      if (clickedDate < this.selectedStartDate) {
        this.selectedEndDate = new Date(this.selectedStartDate);
        this.selectedStartDate = clickedDate;
      } else {
        this.selectedEndDate = clickedDate;
      }
      this.isSelecting = false;
      this.notifyDateRangeChange();
    }

    // Mark for check to ensure UI updates
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }
  formatDate(date: Date | null): string {
    if (!date) { return ''; }
    return `${this.getMonthName(date)} ${date.getDate()}, ${date.getFullYear()}`;
  }

  applySelection(): void {
    if (this.selectedStartDate && this.selectedEndDate) {
      const range = {
        start: this.selectedStartDate,
        end: this.selectedEndDate
      };
      this.dateRangeSelected.emit(range);
      this.onChange(range);
      this.onTouched();
    }
  }

  // Cancel selection
  cancelSelection(): void {
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.isSelecting = false;
    this.onChange(null);
    this.onTouched();
  }

  // Helper methods
  private addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  }

  private getMonthDiff(dateFrom: Date, dateTo: Date): number {
    return (dateTo.getFullYear() - dateFrom.getFullYear()) * 12 +
           dateTo.getMonth() - dateFrom.getMonth();
  }

  private getMonthName(date: Date): string {
    return date.toLocaleString('default', { month: 'long' });
  }

  private notifyDateRangeChange(): void {
    if (this.selectedStartDate && this.selectedEndDate) {
      this.onChange({
        start: this.selectedStartDate,
        end: this.selectedEndDate
      });
      this.onTouched();
    }
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    if (value && value.start && value.end) {
      this.selectedStartDate = new Date(value.start);
      this.selectedEndDate = new Date(value.end);
    } else {
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  // Add this method to date-range-selector.component.ts
  getDaysForMonth(month: Date): Array<{day: number, date: Date, isCurrentMonth: boolean}> {
    const result = [];
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);

    // Add days from previous month
    const firstDayOfWeek = firstDay.getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      const date = new Date(firstDay);
      date.setDate(date.getDate() - (firstDayOfWeek - i));
      result.push({
        day: date.getDate(),
        date: new Date(date),
        isCurrentMonth: false
      });
    }

    // Add days from current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(month.getFullYear(), month.getMonth(), i);
      result.push({
        day: i,
        date: new Date(date),
        isCurrentMonth: true
      });
    }

    // Add days from next month
    const lastDayOfWeek = lastDay.getDay();
    for (let i = 1; i < (7 - lastDayOfWeek); i++) {
      const date = new Date(lastDay);
      date.setDate(date.getDate() + i);
      result.push({
        day: date.getDate(),
        date: new Date(date),
        isCurrentMonth: false
      });
    }

    return result;
  }

  private isSameDay(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }

  isStartDate(date: Date): boolean {
    return this.selectedStartDate && this.isSameDay(date, this.selectedStartDate);
  }

  isEndDate(date: Date): boolean {
    return this.selectedEndDate && this.isSameDay(date, this.selectedEndDate);
  }

  isDateInRange(date: Date): boolean {
    if (!this.selectedStartDate || !this.selectedEndDate) { return false; }

    // Remove time part for comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const startOnly = new Date(this.selectedStartDate.getFullYear(),
      this.selectedStartDate.getMonth(),
      this.selectedStartDate.getDate());
    const endOnly = new Date(this.selectedEndDate.getFullYear(),
      this.selectedEndDate.getMonth(),
      this.selectedEndDate.getDate());

    return dateOnly >= startOnly && dateOnly <= endOnly;
  }

  handleDateClick(day: {day: number, date: Date, isCurrentMonth: boolean}): void {
    console.log('handleDateClick : 247 ', day);
    if (!day.isCurrentMonth) {
      return; // Skip if not in current month
    }

    // Create a new date object to avoid reference issues
    const clickedDate = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate());
    this.onDateClick(clickedDate);
  }
}
